/**
 * @file Month and boundary query helpers for the Recalls route.
 */

import {
    collection,
    getDocs,
    limit,
    orderBy,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";

function mapSnapshotDocs(snapshot) {
    return snapshot.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...snapshotDoc.data(),
    }));
}

/**
 * Returns whether a value is a valid month key.
 * @param {string} monthKey Candidate month key.
 * @returns {boolean} True when the value is YYYY-MM.
 */
export function isValidRecallMonthKey(monthKey) {
    return /^\d{4}-\d{2}$/.test(monthKey || "");
}

/**
 * Returns a YYYY-MM month key for a Date.
 * @param {Date} [date=new Date()] Source date.
 * @returns {string} Month key.
 */
export function getRecallMonthKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

/**
 * Adds whole calendar months to a YYYY-MM month key.
 * @param {string} monthKey Source month key.
 * @param {number} offset Number of months to add.
 * @returns {string} Shifted month key.
 */
export function addRecallMonths(monthKey, offset) {
    const [year, month] = isValidRecallMonthKey(monthKey) ?
        monthKey.split("-").map(Number) :
        [new Date().getFullYear(), new Date().getMonth() + 1];
    return getRecallMonthKey(new Date(year, month - 1 + offset, 1));
}

/**
 * Returns the inclusive upper bound for the default operational query.
 * @returns {string} Next-month key.
 */
export function getRecallsOperationalUpperMonth() {
    return addRecallMonths(getRecallMonthKey(), 1);
}

/**
 * Builds the default active Recalls route query.
 * @returns {object} Firestore query reference.
 */
export function buildOperationalRecallsQuery() {
    return query(
        collection(db, "recalls"),
        where("isClosedResolved", "==", false),
        where("effectiveMonth", "<=", getRecallsOperationalUpperMonth()),
        orderBy("effectiveMonth", "asc"),
        orderBy("recall_date", "asc"),
    );
}

/**
 * Reads the furthest known effective month across all Recalls.
 * @returns {Promise<string|null>} Latest month key, if available.
 */
export async function fetchLatestRecallBoundaryMonth() {
    const snapshot = await getDocs(query(
        collection(db, "recalls"),
        orderBy("effectiveMonth", "desc"),
        limit(1),
    ));
    const monthKey = snapshot.docs[0]?.data()?.effectiveMonth || "";
    return isValidRecallMonthKey(monthKey) ? monthKey : null;
}

/**
 * Reads the earliest known effective month across all Recalls.
 * @returns {Promise<string|null>} Oldest month key, if available.
 */
export async function fetchOldestRecallBoundaryMonth() {
    const snapshot = await getDocs(query(
        collection(db, "recalls"),
        orderBy("effectiveMonth", "asc"),
        limit(1),
    ));
    const monthKey = snapshot.docs[0]?.data()?.effectiveMonth || "";
    return isValidRecallMonthKey(monthKey) ? monthKey : null;
}

/**
 * Fetches one month of active and optionally closed Recalls rows.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @param {object} [options] Fetch options.
 * @param {boolean} [options.includeActive=true] Whether to fetch active rows.
 * @param {boolean} [options.includeClosed=false] Whether to fetch closed rows.
 * @returns {Promise<{activeRows: Array<object>, closedRows: Array<object>}>}
 * Month rows.
 */
export async function fetchRecallsMonthRows(
    monthKey,
    { includeActive = true, includeClosed = false } = {},
) {
    if (!isValidRecallMonthKey(monthKey)) {
        return { activeRows: [], closedRows: [] };
    }

    const activeRowsPromise = includeActive === true ?
        getDocs(query(
            collection(db, "recalls"),
            where("isClosedResolved", "==", false),
            where("effectiveMonth", "==", monthKey),
            orderBy("recall_date", "asc"),
        )).then(mapSnapshotDocs) :
        Promise.resolve([]);

    const closedRowsPromise = includeClosed === true ?
        getDocs(query(
            collection(db, "recalls"),
            where("isClosedResolved", "==", true),
            where("closedMonth", "==", monthKey),
            orderBy("closedAt", "desc"),
        )).then(mapSnapshotDocs) :
        Promise.resolve([]);

    const [activeRows, closedRows] = await Promise.all([
        activeRowsPromise,
        closedRowsPromise,
    ]);

    return { activeRows, closedRows };
}
